import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc, collection, query, where, getDocs } from "firebase/firestore";
import db from "../Firebase";
import { useAuth } from "../AuthContext";
import { parseSources } from "../analytics";

const TEAL   = "#0AADA1";
const AMBER  = "#f59e0b";
const PURPLE = "#6c47ff";

const SOURCES = [
    { key: "direct",    label: "Direct",    icon: "🔗", color: TEAL },
    { key: "whatsapp",  label: "WhatsApp",  icon: "💬", color: "#25D366" },
    { key: "linkedin",  label: "LinkedIn",  icon: "💼", color: "#0A66C2" },
    { key: "facebook",  label: "Facebook",  icon: "📘", color: "#1877F2" },
    { key: "instagram", label: "Instagram", icon: "📸", color: "#E1306C" },
    { key: "twitter",   label: "Twitter/X", icon: "🐦", color: "#1DA1F2" },
    { key: "telegram",  label: "Telegram",  icon: "✈️",  color: "#2CA5E0" },
    { key: "google",    label: "Google",    icon: "🔍", color: "#4285F4" },
    { key: "other",     label: "Other",     icon: "🌐", color: AMBER },
];

function fmt(n = 0) {
    if (n >= 1000) return (n / 1000).toFixed(1) + "k";
    return n;
}

export default function TrafficPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();

    const [cardData, setCardData]   = useState(null);
    const [sources,  setSources]    = useState({});
    const [loading,  setLoading]    = useState(true);
    const [cardOwnerId, setCardOwnerId] = useState(null);

    useEffect(() => {
        const load = async () => {
            try {
                const snap = await getDocs(query(collection(db, "cards"), where("slug", "==", id)));
                if (snap.empty) { setLoading(false); return; }
                const data = snap.docs[0].data();
                setCardData(data);
                setCardOwnerId(data.userId || null);

                const aSnap = await getDoc(doc(db, "analytics", id));
                setSources(aSnap.exists() ? parseSources(aSnap.data()) : {});
            } catch (e) {
                console.error(e);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [id]);

    if (user === undefined || loading) {
        return (
            <div style={s.page}>
                <div style={{ color: "rgba(255,255,255,0.3)", fontSize: "14px", margin: "auto" }}>Loading…</div>
            </div>
        );
    }

    if (!user) {
        return (
            <div style={s.page}>
                <div style={s.gate}>
                    <div style={s.gateIcon}>🔒</div>
                    <h2 style={s.gateTitle}>Sign in required</h2>
                    <button onClick={() => navigate("/")} style={s.gateBtn}>Go Home</button>
                </div>
            </div>
        );
    }

    if (cardOwnerId && user.uid !== cardOwnerId) {
        return (
            <div style={s.page}>
                <div style={s.gate}>
                    <div style={s.gateIcon}>⛔</div>
                    <h2 style={s.gateTitle}>Access denied</h2>
                    <button onClick={() => navigate("/")} style={s.gateBtn}>Go Home</button>
                </div>
            </div>
        );
    }

    const total = Object.values(sources).reduce((a, b) => a + b, 0) || 1;

    const rows = SOURCES
        .map(src => ({ ...src, count: sources[src.key] || 0 }))
        .filter(r => r.count > 0)
        .sort((a, b) => b.count - a.count);

    const hasData = rows.length > 0;

    return (
        <div style={s.page}>
            <div style={s.topBar}>
                <button onClick={() => navigate(`/analytics/${id}`)} style={s.backBtn}>← Back</button>
                <span style={s.topLogo}>myprofilecorner</span>
            </div>

            <div style={s.body}>
                {/* Hero */}
                <div style={s.hero}>
                    <div style={s.heroChip}>Traffic Sources</div>
                    <h1 style={s.heroName}>{cardData?.name || id}</h1>
                    <p style={s.heroSub}>Where your visitors come from</p>
                </div>

                {/* Total visits tile */}
                <div style={s.totalTile}>
                    <div style={s.totalLeft}>
                        <div style={s.totalIconBox}>📡</div>
                        <div>
                            <div style={s.totalNum}>{fmt(total === 1 && !hasData ? 0 : total)}</div>
                            <div style={s.totalLabel}>Total tracked visits</div>
                        </div>
                    </div>
                    <div style={s.totalSub}>{rows.length} source{rows.length !== 1 ? "s" : ""}</div>
                </div>

                {/* Source bars */}
                {hasData ? (
                    <div style={s.card}>
                        <div style={s.cardHeader}>
                            <span style={s.cardTitle}>Breakdown</span>
                            <span style={s.cardBadge}>All time</span>
                        </div>
                        {rows.map(row => {
                            const pct = Math.round((row.count / total) * 100);
                            return (
                                <div key={row.key} style={s.sourceRow}>
                                    <div style={s.sourceLeft}>
                                        <span style={s.sourceIcon}>{row.icon}</span>
                                        <div>
                                            <div style={s.sourceName}>{row.label}</div>
                                            <div style={s.sourceBar}>
                                                <div style={{ ...s.sourceBarFill, width: `${pct}%`, background: row.color }} />
                                            </div>
                                        </div>
                                    </div>
                                    <div style={s.sourceRight}>
                                        <span style={{ ...s.sourceCount, color: row.color }}>{fmt(row.count)}</span>
                                        <span style={s.sourcePct}>{pct}%</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div style={s.empty}>
                        <div style={{ fontSize: "2.5rem", marginBottom: "12px" }}>📡</div>
                        <p style={{ color: "rgba(255,255,255,0.4)", fontSize: "14px" }}>
                            No traffic data yet — share your card to start seeing sources.
                        </p>
                    </div>
                )}

                {/* Share links tip */}
                <div style={s.tipCard}>
                    <div style={s.tipTitle}>Track specific sources</div>
                    <p style={s.tipDesc}>Add <code style={s.code}>?utm_source=whatsapp</code> to your link when sharing on specific platforms:</p>
                    <div style={s.tipLinks}>
                        {["whatsapp", "linkedin", "instagram", "telegram"].map(src => (
                            <div key={src} style={s.tipLink}>
                                <span style={s.tipLinkLabel}>{src}</span>
                                <span style={s.tipLinkUrl}>myprofilecorner.com/{id}?utm_source={src}</span>
                            </div>
                        ))}
                    </div>
                </div>

                <p style={s.footer}>Data updates in real time · myprofilecorner.com</p>
            </div>
        </div>
    );
}

const s = {
    page: {
        minHeight: "100vh", background: "#080b10",
        display: "flex", flexDirection: "column",
        fontFamily: "'Outfit', 'Segoe UI', sans-serif",
    },
    topBar: {
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "16px 24px", borderBottom: "1px solid rgba(255,255,255,0.05)",
        position: "sticky", top: 0, zIndex: 100,
        background: "rgba(8,11,16,0.92)", backdropFilter: "blur(12px)",
    },
    backBtn: {
        background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)",
        color: "#ccc", borderRadius: "10px", padding: "8px 16px",
        fontSize: "13px", fontWeight: "600", cursor: "pointer", fontFamily: "inherit",
    },
    topLogo: { fontSize: "13px", fontWeight: "700", color: "rgba(255,255,255,0.3)" },

    body: { maxWidth: "560px", width: "100%", margin: "0 auto", padding: "0 20px 60px" },

    hero: { textAlign: "center", padding: "40px 0 28px" },
    heroChip: {
        display: "inline-block", background: "rgba(10,173,161,0.12)", color: TEAL,
        fontSize: "10px", fontWeight: "700", letterSpacing: "1.5px", textTransform: "uppercase",
        borderRadius: "100px", padding: "5px 14px", marginBottom: "14px",
    },
    heroName: { fontSize: "26px", fontWeight: "800", color: "#fff", margin: "0 0 6px", letterSpacing: "-0.5px" },
    heroSub: { fontSize: "13px", color: "rgba(255,255,255,0.3)", margin: 0 },

    totalTile: {
        background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)",
        borderRadius: "18px", padding: "18px 20px", marginBottom: "14px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
    },
    totalLeft: { display: "flex", alignItems: "center", gap: "14px" },
    totalIconBox: {
        width: "42px", height: "42px", borderRadius: "12px",
        background: "rgba(10,173,161,0.12)", display: "flex", alignItems: "center",
        justifyContent: "center", fontSize: "20px",
    },
    totalNum: { fontSize: "28px", fontWeight: "800", color: "#fff", lineHeight: 1 },
    totalLabel: { fontSize: "11px", color: "rgba(255,255,255,0.35)", marginTop: "3px", fontWeight: "600" },
    totalSub: { fontSize: "12px", color: TEAL, fontWeight: "700", background: "rgba(10,173,161,0.1)", borderRadius: "100px", padding: "4px 12px" },

    card: {
        background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)",
        borderRadius: "18px", padding: "20px", marginBottom: "14px",
    },
    cardHeader: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "20px" },
    cardTitle: { fontSize: "13px", fontWeight: "700", color: "rgba(255,255,255,0.7)" },
    cardBadge: {
        background: "rgba(10,173,161,0.12)", color: TEAL,
        fontSize: "10px", fontWeight: "700", borderRadius: "100px", padding: "4px 10px",
    },

    sourceRow: {
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "12px 0", borderBottom: "1px solid rgba(255,255,255,0.04)",
    },
    sourceLeft: { display: "flex", alignItems: "center", gap: "12px", flex: 1 },
    sourceIcon: { fontSize: "20px", width: "28px", textAlign: "center" },
    sourceName: { fontSize: "13px", fontWeight: "700", color: "#fff", marginBottom: "6px" },
    sourceBar: {
        width: "160px", height: "5px", background: "rgba(255,255,255,0.06)",
        borderRadius: "100px", overflow: "hidden",
    },
    sourceBarFill: { height: "100%", borderRadius: "100px", transition: "width 0.5s ease" },
    sourceRight: { display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "2px", minWidth: "50px" },
    sourceCount: { fontSize: "16px", fontWeight: "800" },
    sourcePct: { fontSize: "10px", color: "rgba(255,255,255,0.3)", fontWeight: "600" },

    tipCard: {
        background: "rgba(10,173,161,0.05)", border: "1px solid rgba(10,173,161,0.15)",
        borderRadius: "18px", padding: "20px", marginBottom: "14px",
    },
    tipTitle: { fontSize: "13px", fontWeight: "700", color: TEAL, marginBottom: "8px" },
    tipDesc: { fontSize: "12px", color: "rgba(255,255,255,0.4)", marginBottom: "14px", lineHeight: 1.6 },
    code: { background: "rgba(10,173,161,0.15)", color: TEAL, padding: "2px 6px", borderRadius: "5px", fontSize: "11px" },
    tipLinks: { display: "flex", flexDirection: "column", gap: "8px" },
    tipLink: { display: "flex", flexDirection: "column", gap: "2px" },
    tipLinkLabel: { fontSize: "10px", fontWeight: "700", color: "rgba(255,255,255,0.3)", textTransform: "uppercase", letterSpacing: "1px" },
    tipLinkUrl: { fontSize: "11px", color: "rgba(255,255,255,0.5)", wordBreak: "break-all" },

    empty: { textAlign: "center", padding: "40px 20px" },
    footer: { textAlign: "center", fontSize: "11px", color: "rgba(255,255,255,0.15)", marginTop: "24px" },

    gate: {
        background: "rgba(255,255,255,0.04)", borderRadius: "20px",
        padding: "3rem 2rem", textAlign: "center", margin: "auto", maxWidth: "360px",
    },
    gateIcon: { fontSize: "2.5rem", marginBottom: "1rem" },
    gateTitle: { fontSize: "1.3rem", fontWeight: "700", color: "#fff", marginBottom: "1.5rem" },
    gateBtn: {
        padding: "0.75rem 2rem", background: TEAL, color: "#fff",
        border: "none", borderRadius: "12px", fontSize: "14px", fontWeight: "700", cursor: "pointer",
    },
};
